import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IChat, MessageStatusEnum } from "@/types/IChat";
import { ItemLocation } from "@virtuoso.dev/message-list";
import { getChatroomContentV2 } from "@/services/getChatRoomContentV2";
import store from "@/redux/store";
import { getChatroomSearch } from "@/services/getChatroomSearch";
import { IBroadcastFeed } from "@/types/IBroadcastFeed";
import { IBroadCastChannel } from "@/types/IBroadcastChannel";
import { getBroadcastChannelFeedById } from "@/services/getBroadcastChannelFeedById";
import { getBroadcastChannelFeedByIdV2 } from "@/services/getBroadcastChannelFeedByIdV2";
import { getBroadcastPinnedMessage } from "@/services/getBroadcastPinnedMessage";
import { IEmojiReaction } from "@/types/IBroadCastChannelFeed";
import produce from "immer";
import { postPinTrenchMessage } from "@/services/postPinTrenchMessage";

export interface FeedSliceState {
  feeds: IBroadcastFeed[];
  loading: boolean;
  dataSetKey: number;
  initialLocation: ItemLocation;
  hasNewer: boolean;
  isInMiddle: boolean;
  around: boolean;
  searchResults: IChat[];
  toggleSearch: boolean;
  emojiReactionMessageId: number;
  feedMessageId: number;
  pinnedMessage: IBroadcastFeed | null;
  trench: IBroadCastChannel
}

const initialFeedSliceState: FeedSliceState = {
  feeds: [],
  loading: false,
  dataSetKey: Date.now(),
  initialLocation: { index: "LAST", align: "end" },
  hasNewer: false,
  isInMiddle: false,
  around: false,
  searchResults: [],
  toggleSearch: false,
  emojiReactionMessageId: -1,
  feedMessageId: -1,
  pinnedMessage: null,
  trench: {} as IBroadCastChannel
};

export const loadInitialMessages = createAsyncThunk(
  "feed/loadInitialMessages",
  async (channelId: number) => {
    const messages = await getBroadcastChannelFeedByIdV2(channelId.toString());
    return messages;
  }
);

export const loadPinnedMessage = createAsyncThunk(
  "feed/loadPinnedMessage",
  async (channelId: number) => {
    // const messages = await getBroadcastPinnedMessage(channelId);
    // if (messages && messages.length > 0) {
    //   return messages[0];
    // } else {
      return null;
    // }
  }
);

interface PinMessagePayload {
  mwssage: IBroadcastFeed;
  messageId: number;
}

export const setPinMessage = createAsyncThunk(
  "feed/setPinnedMessage",
  async ({ mwssage, messageId }: PinMessagePayload) => {
    let response = null;
    if (messageId === 0)
      response = await postPinTrenchMessage(0, mwssage.channelId);
    else response = await postPinTrenchMessage(messageId, mwssage.channelId);
    if (response && response.status === 201 && messageId !== 0) {
      return mwssage;
    } else {
      return null;
    }
  }
);

const goToMessageWindow = createAsyncThunk(
  "feed/goToMessageWindow",
  async ({ key, channelId }: { key: number; channelId: number }, thunkApi) => {
    const feeds = await getBroadcastChannelFeedByIdV2(
      channelId.toString(),
      `around=${key}`
    );
    const index = feeds.findIndex((x) => x.id === key);
    thunkApi.dispatch(highlightMessage({ key, index }));
    return feeds;
  }
);

export const loadOlderMessages = createAsyncThunk(
  "feed/loadOlderMessages",
  async (channelId: number, thunkApi) => {
    const state = (thunkApi.getState() as any)
      .trenchesFeedSlice as FeedSliceState;
    const firstFeed = state.feeds[0];
    if (firstFeed) {
      const feeds = await getBroadcastChannelFeedByIdV2(
        channelId.toString(),
        `before=${firstFeed.id}`
      );
      return feeds;
    } else {
      return [];
    }
  }
);

export const recieveNewMessage = createAsyncThunk(
  "feed/recieveNewMessage",
  async (newMessage: IBroadcastFeed, thunkApi) => {
    return newMessage;
  }
);

export const deleteMessage = createAsyncThunk(
  "feed/deleteMessage",
  async (deleteMessageObj: { ChannelId: number; MsgId: number }, thunkApi) => {
    return deleteMessageObj;
  }
);

export const editMessage = createAsyncThunk(
  "feed/editMessage",
  async (editMessageObj: IBroadcastFeed, thunkApi) => {
    return editMessageObj;
  }
);

export const loadNewerMessages = createAsyncThunk(
  "feed/loadNewerMessages",
  async (channelId: number, thunkApi) => {
    const state = (thunkApi.getState() as any)
      .trenchesFeedSlice as FeedSliceState;
    const lastFeed = state.feeds[state.feeds.length - 1];
    if (lastFeed) {
      const feeds = await getBroadcastChannelFeedByIdV2(
        channelId.toString(),
        `after=${lastFeed.id}`
      );
      return feeds;
    } else {
      return [];
    }
  }
);

export const searchFeed = createAsyncThunk(
  "feed/searchFeed",
  async (data: { channelId: number; query: string }, thunkApi) => {
    const messages = await getChatroomSearch(
      data.channelId.toString(),
      data.query
    );
    return messages;
  }
);

export const updateEmojis = createAsyncThunk(
  "feed/updateEmojis",
  async (
    data: { messageId: number; _newEmojiContent: IEmojiReaction[] },
    thunkAPI
  ) => {
    return data;
  }
);

export const scrollToMessage = createAsyncThunk(
  "feed/scrollToMessage",
  async ({ key, channelId }: { key: number; channelId: number }, thunkApi) => {
    const state = (thunkApi.getState() as any)
      .trenchesFeedSlice as FeedSliceState;
    const index = state.feeds.findIndex((feed) => feed.id === key);
    if (index > -1) {
      thunkApi.dispatch(highlightMessage({ key, index }));
    } else {
      thunkApi.dispatch(goToMessageWindow({ key, channelId }));
    }
    return { index, align: "center" };
  }
);

export const highlightMessage = createAsyncThunk(
  "chat/highlightMessage",
  async ({ key, index }: { key: number; index: number }) => {
    return { key, index };
  }
);

export const TrenchesFeedSlice = createSlice({
  name: "feed",
  initialState: initialFeedSliceState,
  reducers: {
    setToggleSearch: (state, action: PayloadAction<boolean>) => {
      state.toggleSearch = action.payload;
      if (!action.payload) {
        state.searchResults = [];
      }
    },
    setTrench: (state, action: PayloadAction<IBroadCastChannel>) => {
      state.trench = action.payload;
    },
    setBottomSheet: (state, action: PayloadAction<number>) => {
      state.feedMessageId = action.payload;
    },
    setEmojiReaction: (state, action: PayloadAction<number>) => {
      state.emojiReactionMessageId = action.payload;
    },
    resetChatMessages: (state) => {
      state.feeds = [];
    },
    setIsInMiddle: (state, action: PayloadAction<boolean>) => {
      state.isInMiddle = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadInitialMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadInitialMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.dataSetKey = Date.now();
      state.feeds = action.payload;
      state.isInMiddle = false;
      state.around = false;
    });
    builder.addCase(loadPinnedMessage.fulfilled, (state, action) => {
      state.pinnedMessage = action.payload;
    });
    builder.addCase(setPinMessage.fulfilled, (state, action) => {
      state.pinnedMessage = action.payload;
    });
    builder.addCase(updateEmojis.fulfilled, (state, action) => {
      return produce(state, (draft) => {
        const feed = draft.feeds.find(
          (feed) => feed.id === action.payload.messageId
        );
        if (feed) {
          feed.emojiReactions = action.payload._newEmojiContent;
        }
      });
    });
    builder.addCase(loadOlderMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadOlderMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.isInMiddle = true;
      state.feeds = [...action.payload, ...state.feeds];
    });
    builder.addCase(loadNewerMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadNewerMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.isInMiddle = false;
      state.around = false;
      state.feeds = [...state.feeds, ...action.payload];
    });
    builder.addCase(searchFeed.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchFeed.fulfilled, (state, action) => {
      state.loading = false;
      state.isInMiddle = false;
      state.around = false;
      state.searchResults = action.payload;
    });
    builder.addCase(recieveNewMessage.fulfilled, (state, action) => {
      if (action.payload) {
        // state.feeds = [...state.feeds, action.payload];
        state.hasNewer = false;
      }
    });
    builder.addCase(deleteMessage.fulfilled, (state, action) => {
      state.feeds.filter((feed) => {
        return feed.id !== action.payload.MsgId;
      });
    });
    builder.addCase(editMessage.fulfilled, (state, action) => {
      state.feeds.filter((feed) => {
        if (feed.id === action.payload.id) {
          feed.textContent = action.payload.textContent;
        }
        return feed;
      });
    });
    builder.addCase(goToMessageWindow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(goToMessageWindow.fulfilled, (state, action) => {
      state.loading = false;
      state.feeds = action.payload;
      const key = action.meta.arg.key;
      const index = state.feeds.findIndex((feed) => feed.id === key);
      // reload the list, we're in a different window
      state.dataSetKey = Date.now();
      state.initialLocation = { index, align: "center" };
      state.hasNewer = true;
      state.isInMiddle = true;
      state.around = true;
    });
  },
});

export const {
  setEmojiReaction,
  setToggleSearch,
  setBottomSheet,
  resetChatMessages,
  setTrench,
  setIsInMiddle
} = TrenchesFeedSlice.actions;

export default TrenchesFeedSlice.reducer;
